import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AddManagerModal from '../../components/dashboard/orders/AddManagerModal';
import { removeManager } from '../../api/facility-manager';

ManagerChips.propTypes = {
  params: PropTypes.any,
  onDeleteSuccess: PropTypes.func,
  type: PropTypes.string,
  hasFacilityAccess: PropTypes.bool,
  refetch: PropTypes.func,
  showAllManagers: PropTypes.bool,
};

export default function ManagerChips({ params, onDeleteSuccess, type, hasFacilityAccess, refetch = null, showAllManagers = true }) {
  const [submit, setSubmit] = useState(false);
  const [facilityManagerAssignments, setFacilityManagerAssignments] = useState(params.row.facilityManagerAssignments ?? []);

  useEffect(() => setFacilityManagerAssignments(params.row.facilityManagerAssignments ?? []), [params.row])

  const handleDeleteManager = async (manager) => {
    if (submit) return;
    try {
      setSubmit(true);
      const response = await removeManager(manager.modelId, type, manager.facilityManagerId);
      if (response.status === 200) {
        setFacilityManagerAssignments(fma => fma.filter(x => x.facilityManagerId !== manager.facilityManagerId));
        params.api.updateRows([{
          ...params.row,
          facilityManagerAssignments: params.row.facilityManagerAssignments.filter(x => x.facilityManagerId !== manager.facilityManagerId)
        }]);
        if (onDeleteSuccess) await onDeleteSuccess();
      }
    }
    finally { setSubmit(false); }
  };

  const ManagerChip = ({manager}) => <Chip
    label={manager.manager}
    variant="filled"
    onDelete={onDeleteSuccess == null ? null : async (event) => {
      event.deleteChip = true;
      await handleDeleteManager(manager);
    }}
    color={manager.currentUser ? 'info' : 'primary'}
    sx={{
      opacity: manager.deletedManager ? .5 : 1,
      marginRight: 1,
      border: () => `solid 1px`,
    }}
    size="small"
  />

  const ManagerLabel = ({manager}) => <Box
    sx={{
      px: 0.5,
      marginRight: 0.5,
      border: () => `solid 1px`,
      color: 'primary.main',
      borderRadius: '50px',
    }}
  >
    {manager.manager}
  </Box>

  const getManagerTooltipTitle = (managers) => {
    const hasCurrentManager = managers.some(m => m.currentUser);
    if(hasCurrentManager) return managers.filter(m => !m.currentUser).map(m => m.manager).join(', ');
    return managers.slice(1).map(m => m.manager).join(', ');
  };

  return <Box
    component="div"
    sx={{
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: showAllManagers ? 'wrap' : 'nowrap',
      alignItems: 'center',
      '& .MuiChip-deleteIcon': {
        display: 'none',
      },
      '&:hover .MuiChip-deleteIcon': {
        display: 'block',
      },
      '&:hover .add-manager-modal-button': {
        visibility: 'visible',
        opacity: 1,
      },
    }}
  >
    {showAllManagers ? 
      !submit && facilityManagerAssignments.map((manager) => hasFacilityAccess ?
        <ManagerChip key={manager.facilityManagerId} manager={manager} /> : 
        <ManagerLabel key={manager.facilityManagerId} manager={manager} />
      ) : facilityManagerAssignments.length > 0 ? 
        <>
          {hasFacilityAccess ?
            <ManagerChip manager={facilityManagerAssignments.find(f => f.currentUser) || facilityManagerAssignments[0]} /> : 
            <ManagerLabel manager={facilityManagerAssignments.find(f => f.currentUser) || facilityManagerAssignments[0]} /> 
          }
          {facilityManagerAssignments?.length > 1 && (
            <Tooltip arrow placement="top" title={getManagerTooltipTitle(facilityManagerAssignments)}>
              <Chip label={`+${facilityManagerAssignments.length - 1}`} color="primary" size="small" sx={{mr: 0.5}} />
            </Tooltip>
          )}
        </>
      : null}
    {submit && <LoadingButton loading={submit} />}
    {hasFacilityAccess && onDeleteSuccess != null && <AddManagerModal refetch={refetch} model={params.row} type={type} missingFacility={params.row.facilityId === 0} />}
  </Box>
}

